import { HistoryMetadataByChain, TokenHistoryInfo } from "../../../types/history";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { TokensSecondRow } from "./TokensSecondRow";
import { TokensFirstRow } from "./TokensFirstRow";
import { useMemo } from "react";
import { SEPARATOR_DOT } from "../../../config/consts";
import { Amount } from "types/amount";
import { useIsTradeHistoryReturned } from "hooks/useIsTradeHistoryReturned";
import { SkeletonBlock } from "components/general/Skeleton";
import styled from "@emotion/styled";
import { Hash } from "viem";
import { css } from "@emotion/react";
import { ActivityStore } from "../../../store/activity/ActivityStore";
import { Chain } from "viem/chains";

interface TokenNamesProps {
  tokenHistoryInfo: TokenHistoryInfo;
  chainId: Chain["id"];
}

export const TokensBoughtOrSold: React.FC<TokenNamesProps> = ({ tokenHistoryInfo, chainId }) => {
  const historyTokens = Object.keys(tokenHistoryInfo) as Array<Hash>;
  const historyMetadata = ActivityStore.useState(s => s.historyMetadata);
  const theme = GlobalStore.useState(s => s.theme);
  const isTradeHistoryReturned = useIsTradeHistoryReturned();
  const fullName = historyMetadata[chainId]?.[historyTokens[0]]?.name;

  // If there's more than 1 token we just show X Tokens else we show the full name of the 1 Token
  const header = useMemo(() => {
    if (historyTokens.length > 1) {
      return `${historyTokens.length} Tokens`;
    } else {
      return fullName;
    }
  }, [historyTokens.length, fullName]);

  return (
    <div
      css={css`
        margin-left: 8px;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      `}
    >
      <TokensFirstRow theme={theme}>
        {isTradeHistoryReturned ? (
          header
        ) : (
          <SkeletonFirstRow>
            <SkeletonBlock />
          </SkeletonFirstRow>
        )}
      </TokensFirstRow>
      <TokensSecondRow theme={theme}>
        {isTradeHistoryReturned ? (
          getTokensBoughtOrSoldDescription(
            historyTokens,
            chainId,
            historyMetadata,
            tokenHistoryInfo[historyTokens[0]]?.amount,
            false
          )
        ) : (
          <SkeletonSecondRow>
            <SkeletonBlock />
          </SkeletonSecondRow>
        )}
      </TokensSecondRow>
    </div>
  );
};

// Sub-components

const SkeletonFirstRow = styled.div`
  width: 85px;
  height: 12px;
`;

const SkeletonSecondRow = styled.div`
  margin-top: 4px;
  width: 129px;
  height: 11px;
`;

// Helpers

export const getTokensBoughtOrSoldDescription = (
  tokens: Array<Hash>,
  chainId: Chain["id"],
  historyMetadata: HistoryMetadataByChain,
  firstAmount: Amount | undefined,
  displayAllTokens: boolean
) => {
  if (tokens.length > 1 || firstAmount == null) {
    if (displayAllTokens || tokens.length < 5) {
      return tokens
        .map(tokenAddress => historyMetadata[chainId][tokenAddress]?.symbol)
        .join(` ${SEPARATOR_DOT} `);
    }
    const customDescription = [...tokens.slice(0, 2), `+${tokens.length - 2}`] as Array<Hash>;
    return customDescription
      .map(e => historyMetadata[chainId][e]?.symbol ?? e)
      .join(` ${SEPARATOR_DOT} `);
  }

  const tokenMetadata = ActivityStore.getRawState().historyMetadata[chainId]?.[tokens[0]];
  const firstAmountFormatted: string | undefined = firstAmount.toDisplay(
    tokens[0],
    tokenMetadata?.displayDecimals ?? 5
  );

  return `${firstAmountFormatted} ${historyMetadata[chainId][tokens[0]]?.symbol}`;
};
